// lib/audio-extract-client.ts
type AudioExtractStatusEnvelope = {
  status?: 'processing' | 'ready' | 'error' | 'missing';
  phase?: 'queued' | 'extracting' | 'uploading' | 'ready' | 'error' | 'missing';
  error?: string;
};

type AudioExtractEnvelope = {
  cacheKey?: string;
  status?: AudioExtractStatusEnvelope;
  result?: { audioSrc?: string };
};

export type ExtractAudioFromVideoOptions = {
  sourceSrc: string;
  timeoutMs: number;
  abortSignal?: AbortSignal;
};

const waitForAudioExtractPoll = (ms: number, abortSignal?: AbortSignal): Promise<void> => (
  new Promise((resolve, reject) => {
    if (abortSignal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      window.clearTimeout(timeoutId);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timeoutId = window.setTimeout(() => {
      abortSignal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    abortSignal?.addEventListener('abort', onAbort, { once: true });
  })
);

/**
 * Runs the full /api/audio/extract-from-video protocol (prepare -> status polling -> result)
 * and resolves with the extracted audio source URL.
 */
export const extractAudioFromVideo = async ({
  sourceSrc,
  timeoutMs,
  abortSignal,
}: ExtractAudioFromVideoOptions): Promise<string> => {
  const prepareResponse = await fetch('/api/audio/extract-from-video?mode=prepare', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal: abortSignal,
    body: JSON.stringify({ sourceSrc }),
  });
  if (!prepareResponse.ok) {
    const errorText = await prepareResponse.text().catch(() => '');
    throw new Error(errorText || `Audio extraction prepare failed (${prepareResponse.status}).`);
  }
  const prepareEnvelope = await prepareResponse.json() as AudioExtractEnvelope;
  const cacheKey = typeof prepareEnvelope.cacheKey === 'string' ? prepareEnvelope.cacheKey.trim() : '';
  if (!cacheKey) throw new Error('Audio extraction did not return a cache key.');

  let status = prepareEnvelope.status;
  if (status?.status === 'error') throw new Error(status.error || 'Audio extraction failed.');

  const startedAt = Date.now();
  while (status?.status === 'processing') {
    if (Date.now() - startedAt > timeoutMs) {
      throw new Error('Timed out waiting for audio extraction.');
    }
    await waitForAudioExtractPoll(status.phase === 'extracting' ? 450 : 700, abortSignal);
    const statusResponse = await fetch(
      `/api/audio/extract-from-video?mode=status&cacheKey=${encodeURIComponent(cacheKey)}`,
      { method: 'GET', signal: abortSignal },
    );
    if (!statusResponse.ok) {
      const errorText = await statusResponse.text().catch(() => '');
      throw new Error(errorText || `Audio extraction status failed (${statusResponse.status}).`);
    }
    status = ((await statusResponse.json()) as AudioExtractEnvelope).status;
  }
  if (status?.status === 'error') throw new Error(status.error || 'Audio extraction failed.');
  if (status?.status !== 'ready') throw new Error('Audio extraction did not reach ready state.');

  const resultResponse = await fetch(
    `/api/audio/extract-from-video?mode=result&cacheKey=${encodeURIComponent(cacheKey)}`,
    { method: 'GET', signal: abortSignal },
  );
  if (!resultResponse.ok) {
    const errorText = await resultResponse.text().catch(() => '');
    throw new Error(errorText || `Audio extraction result failed (${resultResponse.status}).`);
  }
  const resultEnvelope = await resultResponse.json() as AudioExtractEnvelope;
  const audioSrc = typeof resultEnvelope.result?.audioSrc === 'string'
    ? resultEnvelope.result.audioSrc.trim()
    : '';
  if (!audioSrc) throw new Error('Audio extraction returned an empty source URL.');
  return audioSrc;
};
